import React, { useState, useRef, useEffect } from 'react';
import { useAppStore, useAnnotationState } from '@/stores/appStore';
import { annotationService } from '@/services/annotationService';
import { useAnnotations } from '@/hooks/useAnnotations';
import { logger } from '@/lib/logger';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useOrganization } from '@clerk/clerk-react';

interface Point {
  x: number;
  y: number;
}

interface ImageAnnotationUploaderProps {
  floorPlanId: string;
  containerRef: React.RefObject<HTMLDivElement>;
  zoom: number;
  position: { x: number; y: number };
  onComplete?: () => void;
}

export function ImageAnnotationUploader({
  floorPlanId,
  containerRef,
  zoom,
  position,
  onComplete,
}: ImageAnnotationUploaderProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [placementPosition, setPlacementPosition] = useState<Point | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get organization for Clerk users
  const { organization } = useOrganization();

  // Get annotation functions from hook
  const { createAnnotation } = useAnnotations(floorPlanId);

  // Get annotation state from store
  const { activeAnnotationType, isAnnotating } = useAnnotationState();
  const setActiveAnnotationType = useAppStore(state => state.setActiveAnnotationType);

  // Convert screen coordinates to floor plan coordinates
  const screenToFloorPlanCoords = (clientX: number, clientY: number): Point | null => {
    if (!containerRef.current) return null;

    const rect = containerRef.current.getBoundingClientRect();
    const x = (clientX - rect.left - position.x) / zoom;
    const y = (clientY - rect.top - position.y) / zoom;

    return { x, y };
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image file size must be less than 5MB');
      return;
    }

    setSelectedFile(file);

    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  // Handle container click/tap for image placement
  const handlePlacement = async (clientX: number, clientY: number) => {
    if (!isAnnotating || activeAnnotationType !== 'image' || !selectedFile || !previewUrl) return;

    const floorPlanCoords = screenToFloorPlanCoords(clientX, clientY);
    if (!floorPlanCoords) return;

    setPlacementPosition(floorPlanCoords);

    // Upload image and create annotation
    try {
      setIsUploading(true);

      // Upload image to storage (pass organization ID for Clerk users)
      const imageUrl = await annotationService.uploadAnnotationImage(
        selectedFile,
        floorPlanId,
        organization?.id,
      );

      // Create annotation with default size
      const annotationData = {
        floor_plan_id: floorPlanId,
        type: 'image' as const,
        data: {
          x: floorPlanCoords.x,
          y: floorPlanCoords.y,
          width: 200,
          height: 150,
          url: imageUrl,
        },
        style: {
          opacity: 1,
        },
      };

      await createAnnotation(annotationData);

      // Reset state
      setSelectedFile(null);
      setPreviewUrl(null);
      setPlacementPosition(null);
      setActiveAnnotationType(null);

      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      logger.error('Failed to create image annotation:', error);
      toast.error('Failed to create image annotation');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle mouse click
  const handleClick = (e: MouseEvent) => {
    handlePlacement(e.clientX, e.clientY);
    e.preventDefault();
    e.stopPropagation();
  };

  // Handle touch tap
  const handleTouchEnd = (e: TouchEvent) => {
    if (e.changedTouches.length === 1) {
      const touch = e.changedTouches[0];
      handlePlacement(touch.clientX, touch.clientY);
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Clean up preview URL
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Add event listeners
  useEffect(() => {
    if (!isAnnotating || activeAnnotationType !== 'image') return;

    const container = containerRef.current;
    if (!container) return;

    // Only add event listeners if we have an image selected
    if (selectedFile && previewUrl) {
      container.addEventListener('click', handleClick);
      container.addEventListener('touchend', handleTouchEnd, { passive: false });
      container.style.cursor = 'crosshair';
    }

    return () => {
      container.removeEventListener('click', handleClick);
      container.removeEventListener('touchend', handleTouchEnd);
      container.style.cursor = '';
    };
  }, [isAnnotating, activeAnnotationType, containerRef, selectedFile, previewUrl]);

  // Auto-open file dialog when image tool is selected
  useEffect(() => {
    if (isAnnotating && activeAnnotationType === 'image' && !selectedFile && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [isAnnotating, activeAnnotationType, selectedFile]);

  // Handle ESC key to cancel
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && activeAnnotationType === 'image') {
        setSelectedFile(null);
        setPreviewUrl(null);
        setPlacementPosition(null);
        setActiveAnnotationType(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeAnnotationType, setActiveAnnotationType]);

  if (!isAnnotating || activeAnnotationType !== 'image') return null;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Image selection UI - removed as file dialog auto-opens on tool selection */}

      {/* Preview and placement instructions */}
      {selectedFile && previewUrl && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[1001] bg-white border rounded-lg shadow-lg p-4">
          <div className="flex items-start gap-4">
            <img src={previewUrl} alt="Preview" className="w-24 h-24 object-cover rounded" />
            <div className="flex-1">
              <p className="text-sm font-medium mb-1">Click to place image</p>
              <p className="text-xs text-gray-500 mb-2">Press ESC to cancel</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  Change
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSelectedFile(null);
                    setPreviewUrl(null);
                    setActiveAnnotationType(null);
                  }}
                  disabled={isUploading}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          {isUploading && <div className="mt-2 text-xs text-gray-500">Uploading image...</div>}
        </div>
      )}
    </>
  );
}
